import { IconArrowInbox } from 'central-icons/IconArrowInbox';

import { MenuItem, RevelioTooltip } from '@revelio/ui';

import {
  downloadData,
  type ExportData,
} from '../Chart/chart-menu/data-download/download-plot-data';
import { POSTING_DISCOVERY_VALID_COLUMNS } from '../Chart/Table/posting-discovery-columns';
import { getTableColumnHeader } from '../Chart/Table/utils/table-helpers';
import { useFileName } from '../Chart/utils/useFileName';
import {
  type TableRow,
  useTableSelection,
} from '../TableSelectionProvider/TableSelectionProvider';

export const buildPostingXlsxRows = (rows: TableRow[]): ExportData => {
  const columns = POSTING_DISCOVERY_VALID_COLUMNS.filter((key) =>
    rows.some((row) => key in row)
  );

  return rows.map((row) =>
    Object.fromEntries(
      columns.map((key) => {
        const val = row[key];
        const cell = (() => {
          if (typeof val === 'string' || typeof val === 'number') return val;
          if (typeof val === 'boolean') return String(val);
          // Some posting fields (e.g. benefits) are string arrays; flatten them
          // to a comma-separated cell rather than dropping them as empty.
          if (Array.isArray(val)) {
            return val
              .filter(
                (entry): entry is string | number =>
                  typeof entry === 'string' || typeof entry === 'number'
              )
              .join(', ');
          }
          return '';
        })();
        return [getTableColumnHeader(key), cell];
      })
    )
  );
};

export const PostingDiscoverySelectedDataExportMenuItem = () => {
  const { selectedRows } = useTableSelection();
  const rows = selectedRows ?? [];
  const fileName = useFileName({ chartTitle: 'Selected' });

  const hasSelection = rows.length > 0;
  const isDisabled = !hasSelection;

  const triggerDownload = () => {
    if (rows.length === 0) return;
    downloadData(buildPostingXlsxRows(rows), fileName);
  };

  const menuItem = (
    <MenuItem
      icon={<IconArrowInbox size={16} />}
      onClick={triggerDownload}
      disabled={isDisabled}
    >
      {hasSelection
        ? `Download Selected (${rows.length})`
        : 'Download Selected'}
    </MenuItem>
  );

  if (!isDisabled) return menuItem;

  return (
    <RevelioTooltip
      label="Select rows from the table to download"
      placement="bottom"
    >
      <span>{menuItem}</span>
    </RevelioTooltip>
  );
};
